/**
 * WeekView — 7일 × 24시간 시간축 그리드
 *
 * - 종일 슬롯 (상단)
 * - 시간축 그리드 (좌측 시각 라벨 + 7컬럼)
 * - 시간 지정 일정: absolute positioned bar (top/height 계산)
 * - 오늘 컬럼 강조 + 현재 시각 가로선
 * - 빈 셀 클릭 시 새 일정 생성 (시작 시각 prefill)
 * - 캘린더 고유 시각 영역 — 직접 구현
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { CalendarEvent, EventChipStyle } from '../../../models/CalendarEvent';
import { cn } from '../../../lib/utils';
import ChipMarkerIcon from './ChipMarkerIcon';
import { weekdayLabels } from '../calendarWeekdays';
import { useCalendarLabels } from '../useCalendarLabels';

interface Props {
  anchorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent, e?: React.MouseEvent) => void;
  /** 빈 시간 셀 클릭 — 그 시각으로 새 일정 (Date 인자) */
  onCellClick?: (date: Date) => void;
  getStyle: (event: CalendarEvent) => EventChipStyle;
}

const HOUR_HEIGHT = 48; // px per hour
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function startOfWeek(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  c.setDate(c.getDate() - c.getDay());
  return c;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function buildDays(anchor: Date): Date[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i); return d;
  });
}

/** 일정이 그 날짜에 표시돼야 하는지 (시간축 또는 종일 슬롯) */
function isOnDay(event: CalendarEvent, day: Date): boolean {
  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const d0 = new Date(day); d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
  return start < d1 && end > d0;
}

/** 일정의 그 날짜 내 표시 영역 (분 단위) — clamp 0~1440 */
function clampToDay(event: CalendarEvent, day: Date): { startMin: number; endMin: number } | null {
  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const d0 = new Date(day); d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
  if (start >= d1 || end <= d0) return null;
  const startMin = Math.max(0, (start.getTime() - d0.getTime()) / 60000);
  const endMin = Math.min(1440, (end.getTime() - d0.getTime()) / 60000);
  return { startMin, endMin };
}

const WeekView: React.FC<Props> = ({ anchorDate, events, onEventClick, onCellClick, getStyle }) => {
  const labels = useCalendarLabels();
  const weekdays = weekdayLabels();
  const days = useMemo(() => buildDays(anchorDate), [anchorDate]);
  const today = new Date();

  const allDayEvents = useMemo(() => events.filter((e) => e.is_all_day), [events]);
  const timedEvents = useMemo(() => events.filter((e) => !e.is_all_day), [events]);

  // 현재 시각 (1분마다 갱신)
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  // 첫 진입 시 09:00 위치로 자동 스크롤
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = HOUR_HEIGHT * 8;
    }
  }, []);

  const handleCellClick = (day: Date, hour: number) => {
    if (!onCellClick) return;
    const d = new Date(day);
    d.setHours(hour, 0, 0, 0);
    onCellClick(d);
  };

  return (
    <div className="flex flex-col h-full">
      {/* ── 요일 헤더 ── */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border shrink-0">
        <div /> {/* 시간 라벨 자리 */}
        {days.map((d, idx) => {
          const isToday = sameDay(d, today);
          const dayLabel = weekdays[d.getDay()];
          return (
            <div key={idx} className="flex flex-col items-center justify-center py-2 border-l border-border">
              <span className={cn(
                'text-meta',
                d.getDay() === 0 && 'text-rose-500',
                d.getDay() === 6 && 'text-sky-500',
                d.getDay() !== 0 && d.getDay() !== 6 && 'text-muted-foreground',
              )}>{dayLabel}</span>
              <span className={cn(
                'flex items-center justify-center size-7 rounded-full text-label font-semibold',
                isToday ? 'bg-primary text-primary-foreground' : 'text-foreground',
              )}>
                {d.getDate()}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── 종일 슬롯 ── */}
      {allDayEvents.length > 0 && (
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border shrink-0 min-h-[28px]">
          <div className="flex items-center justify-end pr-2 text-meta text-muted-foreground"><Trans>All day</Trans></div>
          {days.map((d, idx) => {
            const items = allDayEvents.filter((e) => isOnDay(e, d));
            return (
              <div key={idx} className="border-l border-border p-0.5 flex flex-col gap-0.5">
                {items.map((event) => {
                  const style = getStyle(event);
                  const categoryLabel = labels.category(style.category);
                  return (
                    <button
                      key={event.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event, e); }}
                      className={cn(
                        'flex items-center gap-1 text-meta font-medium px-1.5 py-0.5 rounded-md text-left hover:opacity-80 min-w-0',
                        style.filledBg,
                        style.borderExtra && `border ${style.borderExtra}`,
                      )}
                      style={event.color ? { backgroundColor: event.color, color: '#fff' } : undefined}
                      title={`${categoryLabel} · ${event.title}`}
                    >
                      <ChipMarkerIcon marker={style.marker} className="size-3" />
                      <span className="truncate">{event.title}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── 시간축 그리드 ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        <div className="grid grid-cols-[60px_repeat(7,1fr)] relative" style={{ height: `${HOUR_HEIGHT * 24}px` }}>
          {/* 시간 라벨 컬럼 */}
          <div className="relative border-r border-border">
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute -translate-y-1/2 right-2 text-meta text-muted-foreground tabular-nums"
                style={{ top: `${h * HOUR_HEIGHT}px` }}
              >
                {h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {/* 7일 컬럼 */}
          {days.map((d, dayIdx) => {
            const isToday = sameDay(d, today);
            const dayTimedEvents = timedEvents.filter((e) => isOnDay(e, d));

            // 현재 시각 가로선 (오늘 컬럼만)
            const nowTop = isToday ? (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT : null;

            return (
              <div
                key={dayIdx}
                className={cn(
                  'relative border-l border-border',
                  isToday && 'bg-primary/[0.02]',
                )}
              >
                {/* 1시간 단위 가로선 */}
                {HOURS.map((h) => (
                  <div
                    key={h}
                    onClick={() => handleCellClick(d, h)}
                    className={cn('absolute left-0 right-0 border-t border-border/40 transition-colors', onCellClick && 'cursor-pointer hover:bg-muted/30')}
                    style={{ top: `${h * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                  />
                ))}

                {/* 현재 시각 가로선 */}
                {nowTop !== null && (
                  <div
                    className="absolute left-0 right-0 z-10 pointer-events-none"
                    style={{ top: `${nowTop}px` }}
                  >
                    <div className="h-px bg-rose-500" />
                    <div className="absolute -left-1 -top-1 size-2 rounded-full bg-rose-500" />
                  </div>
                )}

                {/* 시간 지정 일정 막대 */}
                {dayTimedEvents.map((event) => {
                  const range = clampToDay(event, d);
                  if (!range) return null;
                  const style = getStyle(event);
                  const categoryLabel = labels.category(style.category);
                  const top = range.startMin / 60 * HOUR_HEIGHT;
                  const height = Math.max(20, (range.endMin - range.startMin) / 60 * HOUR_HEIGHT - 2);
                  const startTime = new Date(event.start_at).toLocaleTimeString(i18n.locale || 'en', {
                    hour: '2-digit', minute: '2-digit', hour12: false,
                  });
                  return (
                    <button
                      key={event.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event, e); }}
                      className={cn(
                        'absolute left-0.5 right-0.5 rounded-md px-1.5 py-0.5 text-left overflow-hidden hover:opacity-90 shadow-sm',
                        style.filledBg,
                        style.borderExtra && `border ${style.borderExtra}`,
                      )}
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        ...(event.color ? { backgroundColor: event.color, color: '#fff' } : {}),
                      }}
                      title={`${categoryLabel} · ${event.title} (${startTime})`}
                    >
                      <div className="flex items-center gap-1 text-meta font-medium leading-tight min-w-0">
                        <ChipMarkerIcon marker={style.marker} className="size-3" />
                        <span className="truncate">{event.title}</span>
                      </div>
                      {height > 28 && (
                        <div className="text-meta opacity-80 leading-tight">{startTime}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default WeekView;
